import React, { useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Eye } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = [
  '#00e5a0', '#4f8eff', '#7c3aed', '#f97316', '#fbbf24',
  '#ec4899', '#06b6d4', '#84cc16', '#ef4444', '#a855f7',
  '#14b8a6', '#f43f5e', '#8b5cf6', '#eab308',
];

const AnalyseHistory = ({ model, ticker, companyName, analyses, onBack, onViewResults }) => {
  const sorted = useMemo(() => [...analyses].sort((a, b) => a.date.localeCompare(b.date)), [analyses]);

  const scoreEvolution = useMemo(() =>
    sorted.map(a => ({ date: a.date, score: a.totalScore?.percent || 0 })),
  [sorted]);

  const sectionIds = useMemo(() => {
    const ids = new Set();
    sorted.forEach(a => { if (a.sectionScores) Object.keys(a.sectionScores).forEach(id => ids.add(id)); });
    return [...ids];
  }, [sorted]);

  const sectionEvolution = useMemo(() => {
    if (sorted.length < 2) return [];
    return sorted.map(a => {
      const point = { date: a.date };
      sectionIds.forEach(id => { point[id] = a.sectionScores?.[id]?.percent || 0; });
      return point;
    });
  }, [sorted, sectionIds]);

  if (analyses.length === 0) {
    return (
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="analyse-history">
        <Button variant="ghost" onClick={onBack} className="text-cap-text-secondary hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <p className="text-cap-text-muted text-center py-16">Pas assez d'analyses pour afficher l'historique.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-testid="analyse-history">
      <Button variant="ghost" onClick={onBack} className="text-cap-text-secondary hover:text-white" data-testid="history-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>

      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">Suivi — {companyName}</h1>
        <p className="text-cap-text-muted">{ticker} | {analyses.length} analyses</p>
      </div>

      <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
        <h3 className="font-display text-lg font-bold text-white mb-4">Evolution du score total</h3>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={scoreEvolution}>
              <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} formatter={(v) => [`${v.toFixed(0)}%`, 'Score']} />
              <Line type="monotone" dataKey="score" stroke="#00e5a0" strokeWidth={2} dot={{ r: 4, fill: '#00e5a0' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {sectionEvolution.length >= 2 && (
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
          <h3 className="font-display text-lg font-bold text-white mb-4">Evolution par section</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <LineChart data={sectionEvolution}>
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} formatter={(v) => [`${v}%`]} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '11px' }} />
                {sectionIds.map((id, i) => (
                  <Line key={id} type="monotone" dataKey={id} stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} dot={{ r: 3 }} name={id} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5"><h3 className="font-display text-lg font-bold text-white">Comparatif</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                <th className="py-3 px-4">Date</th><th className="py-3 px-4">Mode</th><th className="py-3 px-4">Score</th><th className="py-3 px-4">Verdict</th><th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(a => {
                const pct = a.totalScore?.percent || 0;
                const icon = pct >= 75 ? '\u{1F7E2}' : pct >= 50 ? '\u{1F7E1}' : '\u{1F534}';
                return (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 px-4 text-white text-sm">{a.date}</td>
                    <td className="py-3 px-4 text-cap-text-secondary text-sm">{a.mode === 'qualite' ? 'Qualité LT' : 'Fort Potentiel'}</td>
                    <td className="py-3 px-4 text-white font-mono font-bold">{pct.toFixed(0)}%</td>
                    <td className="py-3 px-4 text-lg">{icon}</td>
                    <td className="py-3 px-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onViewResults(a)} className="text-cap-text-secondary hover:text-white"><Eye className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyseHistory;
